// ExportButton.jsx
import React, { useState, useRef, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { motion, AnimatePresence } from 'framer-motion';

// Custom hook to get the previous value
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}



const ExportButton = React.forwardRef(
  ({ onClick, disabled, title, Icon, children, onActionComplete }, ref) => {
    const [buttonState, setButtonState] = useState('idle');
    const tippyRef = useRef(null);

    const previousButtonState = usePrevious(buttonState);

    const SUCCESS_ANIMATION_DURATION = 800; // Duration of success animation (ms)
    const ICON_REVERT_DURATION = 600;       // Duration of icon revert animation (ms)


    useEffect(() => {
      return () => {
        // Cleanup on unmount
        if (tippyRef.current) {
          tippyRef.current.destroy();
          tippyRef.current = null;
        }
      };
    }, []);


    const handleClick = async () => {
      if (disabled || buttonState === 'loading') return;
      setButtonState('loading');
      let finalState = 'idle';
      try {
        await onClick();
        finalState = 'success';
      } catch (error) {
        // Export error
        finalState = 'error';
      }

      // Update the button state after the async operation
      setButtonState(finalState);

      if (finalState === 'success') {
        // Wait for the success animation to complete
        setTimeout(() => {
          // Revert back to the original icon
          setButtonState('idle');

          // Wait for the icon revert animation to complete
          setTimeout(() => {
            if (onActionComplete) {
              onActionComplete();
            }
          }, ICON_REVERT_DURATION);
        }, SUCCESS_ANIMATION_DURATION);
      } else {
        // For error or other states, reset the button state after 2 seconds
        setTimeout(() => {
          setButtonState('idle');
        }, 2000);
      }
    };

    // Determine icon to display and animation direction
    const getIcon = () => {
      let icon;
      switch (buttonState) {
        case 'loading':
          // Return a loading spinner icon
          icon = (
            <svg
              className="h-5 w-5 mr-2 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 50 50"
            >
              <motion.circle
                cx="25"
                cy="25"
                r="20"
                stroke="currentColor"
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
                animate={{ pathLength: [0, 1], rotate: [0, 360] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            </svg>
          );
          break;
        case 'success':
          // Return a checkmark icon
          icon = (
            <svg
              className="w-5 h-5 mr-2 text-green-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
            >
              <motion.path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5 }}
              />
            </svg>
          );
          break;
        case 'error':
          // Return an error icon
          icon = (
            <svg
              className="w-5 h-5 mr-2 text-red-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
            >
              <motion.path
                d="M6 18L18 6M6 6l12 12"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5 }}
              />
            </svg>
          );
          break;
        default:
          // Default icon
          icon = (
            <Icon
              className="w-5 h-5 mr-2"
              aria-hidden="true"
            />
          );
          break;
      }

      // Determine the direction of the animation
      const direction =
        (previousButtonState === 'success' || previousButtonState === 'error') &&
        buttonState === 'idle'
          ? 'revert'
          : 'normal';

      // Define animation variants
      const variants = {
        initial: (custom) => ({
          opacity: 0,
          scale: 0.8,
          rotate: custom === 'revert' ? 45 : -45,
        }),
        animate: {
          opacity: 1,
          scale: 1,
          rotate: 0,
        },
        exit: (custom) => ({
          opacity: 0,
          scale: 0.8,
          rotate: custom === 'revert' ? -45 : 45,
        }),
      };

      return (
        <motion.div
          key={buttonState}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          custom={direction}
          transition={{ duration: 0.2 }}
          className="flex items-center"
        >
          {icon}
        </motion.div>
      );
    };

    // Determine button styles based on state
    const getButtonStyles = () => {
      let styles = 'flex items-center w-full px-4 py-2 text-sm text-gray-600 rounded-md ';
      if (disabled) {
        styles += 'opacity-50 cursor-not-allowed pointer-events-none';
      } else if (buttonState === 'loading') {
        styles += 'cursor-wait';
      } else {
        styles += 'hover:bg-blue-500 hover:text-white cursor-pointer';
      }
      return styles;
    };

    // Extract button content for reuse
    const buttonContent = (
      <button
        onClick={handleClick}
        // Do not set the 'disabled' attribute to allow Tippy to work
        ref={ref}
        className={getButtonStyles()}
        aria-disabled={disabled || buttonState === 'loading'}
      >
        <AnimatePresence mode="wait" initial={false}>
          {getIcon()}
        </AnimatePresence>
        <span>{children}</span>
      </button>
    );

    if (title && disabled) {
      return (
        <Tippy
          content={title}
          placement="right"
          arrow={false}
          delay={[200, 0]}
          onMount={(instance) => {
            tippyRef.current = instance;
          }}
          onHide={(instance) => {
            tippyRef.current = null;
          }}
        >
          <div>{buttonContent}</div>
        </Tippy>
      );
    } else {
      return <div>{buttonContent}</div>;
    }
  }
);

ExportButton.displayName = 'ExportButton';

export default ExportButton;
